import type { CarpetType, ServiceCatalogKey, ServiceRequestItem } from "./customer-requests";
import type { CustomerMatchingPreference } from "./customer-requests";

export const ORDER_ASSISTANT_ROUTES = Object.freeze({
  interpret: "/api/v1/customer/order-assistant/interpret",
  transcribe: "/api/v1/customer/order-assistant/transcribe",
  speech: "/api/v1/customer/order-assistant/speech",
  guideSpeech: "/api/v1/customer/order-assistant/guide-speech",
} as const);

export const CUSTOMER_STAGE_VOICE_GUIDES = Object.freeze({
  login: "شماره موبایلتان را وارد کنید و دکمه دریافت کد را بزنید. من در مرحله بعد هم همراهتان هستم.",
  otp: "کد شش‌رقمی پیامک‌شده به‌صورت خودکار در جای خودش قرار می‌گیرد. اگر این اتفاق نیفتاد، کد را دستی وارد کنید.",
  home: "خوش آمدید. برای ثبت سفارش تازه، دکمه ثبت درخواست را بزنید. من قدم‌به‌قدم همراهتان می‌مانم.",
  requests: "اینجا درخواست‌های شماست. می‌توانید وضعیت هر سفارش را ببینید یا درخواست تازه‌ای ثبت کنید.",
  messages: "اینجا پیام‌ها و گفتگوهای شما با پشتیبانی و قالیشویی است. پیام تازه را از همین بخش ارسال کنید.",
  account: "در این صفحه اطلاعات پروفایل، حساب و راه‌های ارتباطی شما قرار دارد.",
  newOrder: "برای ثبت سفارش، خیلی ساده بگویید چه چیزی برای شست‌وشو دارید. من فقط یک سؤال کوتاه در هر مرحله می‌پرسم.",
  profileName: "ابتدا نام و نام خانوادگی خود را وارد کنید.",
  profileCity: "حالا شهر محل دریافت سفارش را انتخاب کنید.",
  profileAddress: "نشانی کامل محل دریافت را بنویسید.",
  profileLocation: "برای ثبت دقیق نشانی، موقعیت را روی نقشه مشخص کنید.",
  profileComplete: "اطلاعات نشانی کامل است. می‌توانید آن را ذخیره کنید.",
  requestDraft: "این درخواست هنوز پیش‌نویس است. اطلاعات باقی‌مانده را کامل کنید.",
  requestWaiting: "درخواست شما ثبت شده و در حال پیدا کردن قالیشویی مناسب بر اساس کیفیت، سرعت و انتخاب اقتصادی است.",
  requestOffered: "پیشنهاد قالیشویی آماده است. جزئیات را ببینید و انتخاب کنید.",
  requestActive: "سفارش فعال است. وضعیت جمع‌آوری و شست‌وشو را از همین صفحه دنبال کنید.",
  requestCompleted: "این سفارش کامل شده است. اگر مایل بودید تجربه خود را ثبت کنید.",
  requestUnknown: "جزئیات و آخرین وضعیت درخواست در این صفحه نمایش داده می‌شود.",
} as const);

export type CustomerStageVoiceGuideKey = keyof typeof CUSTOMER_STAGE_VOICE_GUIDES;

export interface CustomerStageVoiceGuideResponse extends OrderAssistantSpeechResponse {
  readonly key: CustomerStageVoiceGuideKey;
  readonly text: string;
}

export interface OrderAssistantTranscribeRequest {
  readonly audioBase64: string;
  readonly audioMimeType: "audio/webm" | "audio/mp4" | "audio/mpeg" | "audio/wav" | "audio/ogg" | "audio/x-m4a";
}

export interface OrderAssistantTranscribeResponse {
  readonly transcript: string;
}

export interface OrderAssistantSpeechRequest {
  readonly text: string;
}

export interface OrderAssistantSpeechResponse {
  readonly audioBase64: string | null;
  readonly audioMimeType: "audio/mpeg" | "audio/wav" | null;
}

export interface OrderAssistantConversationMessage {
  readonly role: "assistant" | "customer";
  readonly text: string;
}

export interface OrderAssistantMatchingPreferences {
  readonly priority: CustomerMatchingPreference | "unknown";
  readonly maxReturnWaitDays: number | null;
  readonly needPickup: boolean | null;
  readonly needDelivery: boolean | null;
  readonly floorCount: number | null;
  readonly hasElevator: boolean | null;
}

/** Explicit server-owned state for the rebuilt, deterministic conversation. */
export type OrderAssistantConversationPhase =
  | "item"
  | "item_type"
  | "item_count"
  | "item_size"
  | "more_items_first"
  | "more_items_final"
  | "matching_priority"
  | "matching_logistics"
  | "matching_floor"
  | "location"
  | "review";

export interface OrderAssistantConversationState {
  readonly version: 2;
  readonly phase: OrderAssistantConversationPhase;
  readonly turn: number;
  readonly retryCount: number;
  readonly activeDraftIndex: number | null;
  readonly draftItems: readonly OrderAssistantDraftItem[];
  readonly matchingPreferences: OrderAssistantMatchingPreferences;
}

export interface OrderAssistantDraftUnit {
  readonly quantity: number;
  readonly lengthM: number | null;
  readonly widthM: number | null;
  /** Area of each piece. Null means the assistant must ask for it. */
  readonly areaSqm: number | null;
}

/** A partially understood service item that survives across conversation turns. */
export interface OrderAssistantDraftItem {
  readonly reference: string;
  readonly itemType: CarpetType | null;
  readonly count: number | null;
  readonly units: readonly OrderAssistantDraftUnit[];
  readonly services: readonly ServiceCatalogKey[];
  readonly measurementStatus?: "provided" | "estimated" | "onsite" | "pending";
}

export interface OrderAssistantInterpretRequest {
  readonly message: string;
  /** Opt in to the rebuilt state-machine assistant. Old clients stay on v1. */
  readonly conversationVersion?: 2;
  readonly conversation?: OrderAssistantConversationState;
  /** Old clients default to inline audio; newer clients fetch speech separately. */
  readonly includeAudio?: boolean;
  readonly currentItems?: readonly ServiceRequestItem[];
  readonly currentNotes?: string | null;
  readonly pendingMissing?: readonly string[];
  readonly conversationTurn?: number;
  readonly hasLocation?: boolean;
  readonly draftItems?: readonly OrderAssistantDraftItem[];
  readonly conversationHistory?: readonly OrderAssistantConversationMessage[];
  readonly moreItemsAnswer?: "unknown" | "yes" | "no";
  readonly moreItemsReminders?: number;
  readonly noProgressCount?: number;
  readonly matchingPreferences?: OrderAssistantMatchingPreferences;
}

export interface OrderAssistantInterpretResponse {
  readonly reply: string;
  readonly items: readonly ServiceRequestItem[];
  readonly notes: string | null;
  readonly readyForReview: boolean;
  readonly needsLocation: boolean;
  readonly needsHumanSupport: boolean;
  readonly supportReason: string | null;
  readonly supportNotified: boolean;
  readonly understood: readonly string[];
  readonly missing: readonly string[];
  readonly source: "local" | "avalai";
  readonly draftItems?: readonly OrderAssistantDraftItem[];
  readonly moreItemsAnswer?: "unknown" | "yes" | "no";
  readonly moreItemsReminders?: number;
  readonly noProgressCount?: number;
  readonly matchingPreferences?: OrderAssistantMatchingPreferences;
  readonly assistantPrompt?: OrderAssistantPromptKind | null;
  readonly conversation?: OrderAssistantConversationState;
  /** Inline speech for backward compatibility. New clients use the speech route. */
  readonly audioBase64?: string | null;
  readonly audioMimeType?: "audio/mpeg" | "audio/wav" | null;
}

export type OrderAssistantPromptKind =
  | "more_items"
  | "matching_priority"
  | "matching_logistics"
  | "matching_floor"
  | "review";

export interface SpokenSizeAnswer {
  readonly lengthM: number | null;
  readonly widthM: number | null;
  readonly areaSqm: number;
}

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const AR_DIGITS = "٠١٢٣٤٥٦٧٨٩";

export function normalizePersianOrderText(value: string): string {
  return value
    .normalize("NFKC")
    .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED]/g, "")
    .replace(/[۰-۹]/g, (digit) => String(FA_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(AR_DIGITS.indexOf(digit)))
    .replace(/ي/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/ۀ/g, "ه")
    .replace(/\s+/g, " ")
    .trim();
}

const NUMBER_WORDS: Readonly<Record<string, number>> = Object.freeze({
  یک: 1, یه: 1, دو: 2, سه: 3, چهار: 4, پنج: 5, شش: 6, هفت: 7, هشت: 8,
  نه: 9, ده: 10, یازده: 11, دوازده: 12, سیزده: 13, چهارده: 14,
  پانزده: 15, شانزده: 16, هفده: 17, هجده: 18, نوزده: 19, بیست: 20,
});

const ITEM_PATTERNS: readonly {
  readonly type: CarpetType;
  readonly patterns: readonly RegExp[];
  readonly defaultService: ServiceCatalogKey;
  readonly usesArea: boolean;
  readonly label: string;
}[] = [
  { type: "machine_made", patterns: [/فرش ماشینی/g, /قالی ماشینی/g], defaultService: "washing_standard", usesArea: true, label: "فرش ماشینی" },
  { type: "handmade", patterns: [/فرش دستباف(?:ت)?/g, /قالی دستباف(?:ت)?/g], defaultService: "washing_delicate", usesArea: true, label: "فرش دستباف" },
  { type: "silk", patterns: [/فرش ابریشم(?:ی)?/g, /ابریشم/g], defaultService: "washing_silk", usesArea: true, label: "فرش ابریشم" },
  { type: "kilim", patterns: [/گلیم/g, /جاجیم/g], defaultService: "washing_kilim", usesArea: true, label: "گلیم یا جاجیم" },
  { type: "gabbeh", patterns: [/گبه/g], defaultService: "washing_gabbeh", usesArea: true, label: "گبه" },
  { type: "moket", patterns: [/موکت/g], defaultService: "washing_moket", usesArea: true, label: "موکت" },
  { type: "tableau", patterns: [/تابلو ?فرش/g], defaultService: "washing_tableau", usesArea: true, label: "تابلوفرش" },
  { type: "sofa", patterns: [/مبل(?:مان)?/g, /کاناپه/g], defaultService: "washing_sofa", usesArea: false, label: "مبل" },
  { type: "curtain", patterns: [/پرده/g], defaultService: "washing_curtain", usesArea: false, label: "پرده" },
  { type: "mattress", patterns: [/تشک/g, /خوشخواب/g], defaultService: "washing_mattress", usesArea: false, label: "تشک یا خوشخواب" },
  { type: "blanket", patterns: [/پتو/g, /روتختی/g], defaultService: "washing_blanket", usesArea: false, label: "پتو یا روتختی" },
];

const EXTRA_SERVICES: readonly { readonly patterns: readonly RegExp[]; readonly service: ServiceCatalogKey }[] = [
  { patterns: [/لکه/g, /چرک مرده/g, /چای/g, /قهوه/g, /روغن/g], service: "stain_removal" },
  { patterns: [/ضدعفونی/g, /ضد عفونی/g, /حساسیت/g], service: "disinfect" },
  { patterns: [/نانو/g, /محافظ/g], service: "protection_nano" },
  { patterns: [/رفو/g, /پارگی/g, /سوراخ/g], service: "repair_hole" },
  { patterns: [/ریشه/g], service: "fringe_restore" },
  { patterns: [/خشک ?شویی/g, /در محل/g], service: "dry_clean_on_site" },
];

export function parseOrderNumber(token: string | undefined): number | null {
  if (!token) return null;
  const normalized = normalizePersianOrderText(token).replace("/", ".");
  if (/^\d+(?:\.\d+)?$/.test(normalized)) return Number(normalized);
  return NUMBER_WORDS[normalized] ?? null;
}

function numberFromToken(token: string | undefined): number | null {
  return parseOrderNumber(token);
}

const AREA_UNIT_PATTERN = String.raw`متر(?:\s*مربع|مربع|ی|ن|ه)?`;
const EACH_PIECE_PATTERN = /هر\s*(?:کدام|کدوم|یک|دو(?:تا)?(?:شون|ش)?)|هردو(?:تا)?(?:شون|ش)?/u;
const SIZE_NUMBER_PATTERN = String.raw`(\d+(?:[./]\d+)?|یک|یه|دو|سه|چهار|پنج|شش|هفت|هشت|نه|ده|یازده|دوازده|سیزده|چهارده|پانزده|شانزده|هفده|هجده|نوزده|بیست)`;
const METER_UNIT_PATTERN = String.raw`متر(?:\s*مربع|مربع|ی|ن|ه)?`;

/** Collapse Android STT quirks so size follow-ups can be matched. */
export function normalizeSpokenSizeTranscript(value: string): string {
  return normalizePersianOrderText(value)
    .replace(/[\u200c\u200d\u0640]/g, "")
    .replace(/[.!؟?،,;؛:"'«»()[\]]/g, " ")
    .replace(/متر\s+ن\b/gu, "مترن")
    .replace(/متر\s+ه\b/gu, "متره")
    .replace(/مترمربع/gu, "متر مربع")
    .replace(/هر\s*دو\s*تا(?:شون|یش|ش)?/gu, "هر دو")
    .replace(/هر\s*دوتا(?:شون|یش|ش)?/gu, "هر دو")
    .replace(/هردوتا(?:شون|یش|ش)?/gu, "هر دو")
    .replace(/هر\s*دوتاشون/gu, "هر دو")
    .replace(/هر\s*کدوم/gu, "هر کدام")
    .replace(/\b(?:خب|یعنی|دیگه|دیگر|تقریبا(?:ً)?|حدودا(?:ً)?)\b/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Parse a size-only customer reply, including messy Android transcripts
 * like «هر دو ۱۲ مترن», «هردوتا 12متر», «دو در سه» and «دوازده متری».
 */
export function parseSpokenSizeAnswer(message: string): SpokenSizeAnswer | null {
  const text = normalizeSpokenSizeTranscript(message);
  if (!text) return null;

  const dimensions = text.match(new RegExp(
    `${SIZE_NUMBER_PATTERN}\\s*(?:در|x|×)\\s*${SIZE_NUMBER_PATTERN}(?:\\s*${METER_UNIT_PATTERN})?`,
    "u",
  ));
  if (dimensions) {
    const width = parseOrderNumber(dimensions[1]);
    const length = parseOrderNumber(dimensions[2]);
    if (width && length) {
      return { lengthM: length, widthM: width, areaSqm: Math.round(width * length * 100) / 100 };
    }
  }

  const area = text.match(new RegExp(
    `${SIZE_NUMBER_PATTERN}\\s*${METER_UNIT_PATTERN}`,
    "u",
  ));
  if (area) {
    const value = parseOrderNumber(area[1]);
    if (value && value > 0 && value <= 10_000) {
      return { lengthM: null, widthM: null, areaSqm: value };
    }
  }

  const bare = text.match(new RegExp(
    `^(?:هر\\s*(?:دو|کدام|کدوم|یک)\\s+)?(\\d+(?:[./]\\d+)?|یازده|دوازده|سیزده|چهارده|پانزده|شانزده|هفده|هجده|نوزده|بیست)$`,
    "u",
  ));
  if (bare) {
    const value = parseOrderNumber(bare[1]);
    if (value && value >= 2 && value <= 80) {
      return { lengthM: null, widthM: null, areaSqm: value };
    }
  }
  return null;
}

function nearestNumberBefore(text: string, start: number): number | null {
  const prefix = text.slice(Math.max(0, start - 32), start);
  const match = prefix.match(/(?:^|\s)(\d+(?:\.\d+)?|[آ-ی]+?)(?:\s*(?:عدد|تخته|تا(?:ش|یش|تاشون)?|یش|دانه|دست|سرویس|ست))?\s*$/);
  return numberFromToken(match?.[1]);
}

function areaNear(text: string, start: number, end: number, count: number): number | null {
  const neighborhood = text.slice(Math.max(0, start - 28), Math.min(text.length, end + 50));
  const areaMatch = neighborhood.match(new RegExp(`(\\d+(?:\\.\\d+)?|[آ-ی]+)\\s*(?:${AREA_UNIT_PATTERN})`, "u"));
  if (!areaMatch) return null;
  const area = numberFromToken(areaMatch[1]);
  if (area === null) return null;
  if (!Number.isFinite(area) || area <= 0) return null;
  return EACH_PIECE_PATTERN.test(neighborhood) ? area * count : area;
}

function areaMention(text: string): number | null {
  const match = text.match(new RegExp(`(\\d+(?:\\.\\d+)?|[آ-ی]+)\\s*(?:${AREA_UNIT_PATTERN})`, "u"));
  return numberFromToken(match?.[1]);
}

function areaQuestionLabel(value: string): string | null {
  if (value.startsWith("متراژ تقریبی ")) return value.replace("متراژ تقریبی ", "");
  if (value.startsWith("اندازه ")) return value.replace("اندازه ", "");
  return null;
}

function definitionForType(type: CarpetType) {
  return ITEM_PATTERNS.find((entry) => entry.type === type);
}

function countUnitForType(type: CarpetType): string {
  if (type === "sofa") return "دست/سرویس";
  if (type === "curtain" || type === "mattress" || type === "other") return "عدد";
  return "تخته";
}

function dedupeServices(values: readonly ServiceCatalogKey[]): ServiceCatalogKey[] {
  return [...new Set(values)];
}

export function interpretPersianOrder(input: OrderAssistantInterpretRequest): OrderAssistantInterpretResponse {
  const text = normalizePersianOrderText(input.message);
  const found: { item: ServiceRequestItem; label: string; position: number; missingArea: boolean }[] = [];
  const globalServices = EXTRA_SERVICES
    .filter((entry) => entry.patterns.some((pattern) => { pattern.lastIndex = 0; return pattern.test(text); }))
    .map((entry) => entry.service);

  for (const definition of ITEM_PATTERNS) {
    for (const pattern of definition.patterns) {
      pattern.lastIndex = 0;
      const match = pattern.exec(text);
      if (!match) continue;
      const mentionedCount = nearestNumberBefore(text, match.index);
      const count = Math.max(1, Math.round(mentionedCount ?? 1));
      const area = definition.usesArea ? areaNear(text, match.index, match.index + match[0].length, count) : count;
      found.push({
        position: match.index,
        label: definition.label,
        missingArea: definition.usesArea && area === null,
        item: {
          itemType: definition.type,
          count,
          approximateSqm: area ?? (definition.usesArea ? 1 : count),
          services: dedupeServices([definition.defaultService, ...globalServices]),
        },
      });
      break;
    }
  }

  // Colloquial split: «سه فرش دارم؛ دو تاش ماشینی و یکیش دست‌بافه».
  // The subtype can be separated from the word فرش, so parse it explicitly.
  if (/(?:فرش|قالی)/u.test(text)) {
    const splitTypes = [
      { type: "machine_made" as const, pattern: /ماشینی/u },
      { type: "handmade" as const, pattern: /دست\s*باف(?:ت)?/u },
    ];
    for (const split of splitTypes) {
      if (found.some((entry) => entry.item.itemType === split.type)) continue;
      const match = split.pattern.exec(text);
      if (!match) continue;
      const count = nearestNumberBefore(text, match.index);
      if (count === null) continue;
      const definition = definitionForType(split.type)!;
      // A later area may belong to the next item (for example «... دست‌بافه، ۶ متر موکت»).
      // Keep split-carpet dimensions for on-site measurement unless they are stated
      // in an explicit «فرش ماشینی ... متر» phrase handled by the main parser.
      const area = null;
      found.push({
        position: match.index,
        label: definition.label,
        missingArea: area === null,
        item: {
          itemType: split.type,
          count,
          approximateSqm: area ?? 1,
          services: dedupeServices([definition.defaultService, ...globalServices]),
          measurementStatus: area === null ? "onsite" : "estimated",
        },
      });
    }
  }

  found.sort((a, b) => a.position - b.position);
  let items = [...(input.currentItems ?? [])];
  let missing = [...(input.pendingMissing ?? [])];
  const understood = found.map((entry) => {
    const area = entry.missingArea ? "" : `، حدود ${entry.item.approximateSqm} متر`;
    return `${entry.item.count} ${countUnitForType(entry.item.itemType)} ${entry.label}${area}`;
  });

  for (const entry of found) {
    const existing = items.findIndex((item) => item.itemType === entry.item.itemType);
    if (existing >= 0) items[existing] = entry.item;
    else items.push(entry.item);
    const areaQuestion = `متراژ تقریبی ${entry.label}`;
    missing = missing.filter((value) => value !== areaQuestion);
    if (entry.missingArea) missing.push(areaQuestion);
  }

  if (found.length === 0 && globalServices.length > 0 && items.length > 0) {
    items = items.map((item) => ({ ...item, services: dedupeServices([...item.services, ...globalServices]) }));
    understood.push("خدمات تکمیلی درخواستی");
  }

  if (found.length === 0 && missing.length > 0) {
    const spoken = parseSpokenSizeAnswer(text);
    const area = spoken?.areaSqm ?? areaMention(text);
    const nextQuestion = missing.find((value) => areaQuestionLabel(value) !== null);
    if (area !== null && nextQuestion) {
      const label = areaQuestionLabel(nextQuestion)!;
      const definition = ITEM_PATTERNS.find((entry) => entry.label === label || entry.label.replace("دستباف", "دست‌بافت") === label);
      const matchingItems = definition
        ? items.filter((item) => item.itemType === definition.type)
        : items.filter((item) => ITEM_PATTERNS.find((entry) => entry.type === item.itemType)?.usesArea);
      if (matchingItems.length > 0) {
        const perPiece = EACH_PIECE_PATTERN.test(text);
        items = items.map((item) => {
          const usesArea = ITEM_PATTERNS.find((entry) => entry.type === item.itemType)?.usesArea;
          if (!usesArea) return item;
          if (definition && item.itemType !== definition.type) return item;
          const approximateSqm = perPiece ? area * item.count : area;
          return { ...item, approximateSqm, measurementStatus: "provided" as const };
        });
        missing = missing.filter((value) => value !== nextQuestion);
        understood.push(`متراژ تقریبی ${label}، ${area} متر`);
      }
    }
  }

  missing = [...new Set(missing)];
  if (items.length === 0) missing = ["نوع خدمت یا مورد"];
  const noteParts = [input.currentNotes?.trim(), text].filter((part): part is string => Boolean(part));
  const notes = noteParts.length > 0 ? [...new Set(noteParts)].join(" · ") : null;
  const readyForReview = items.length > 0 && missing.length === 0;
  const summary = items.map((item) => {
    const definition = definitionForType(item.itemType);
    const area = definition?.usesArea ? `، حدود ${item.approximateSqm} متر` : "";
    return `${item.count} ${countUnitForType(item.itemType)} ${definition?.label ?? "مورد"}${area}`;
  });
  const reply = items.length === 0
    ? "چه چیزی برای شست‌وشو دارید؟ مثلاً بگویید: دو تخته فَرش ماشینی یا یک دست مبل."
    : missing.length > 0
      ? `${understood.length > 0 ? understood.join(" و ") : summary.join(" و ")} را فهمیدم. لطفاً ${missing[0]} را هم بگویید.`
      : `${summary.join(" و ")} را ثبت کردم. خلاصه را بررسی کنید و اگر درست است بگویید «تأیید نهایی».`;
  return {
    reply,
    items,
    notes,
    readyForReview,
    needsLocation: false,
    needsHumanSupport: false,
    supportReason: null,
    supportNotified: false,
    understood,
    missing,
    source: "local",
  };
}

export const DEFAULT_CUSTOMER_ORDER_QUESTION = "چه چیزی برای شست‌وشو دارید؟";

/** Internal TTS/prompt teaching that must never be spoken to the customer. */
const PRONUNCIATION_TEACHING_PATTERN =
  /(?:کسره|فتحه|تلفظ\s*صحیح|تلفظ\s*کن(?:ید)?|درست\s*تلفظ|نحوه(?:ٔ)?\s*تلفظ|اعراب[‌\s]*گذار|با\s*اعراب|کافِ?\s*کسره|واژهٔ?\s+\S+\s+را\s+با|آموزش\s*خواندن|موتور\s*صدا|واکه(?:‌ها)?\s*را\s*نکش|مکث(?:‌ها)?\s*کوتاه\s*باشد|هیچ\s+(?:توضیح(?:ی)?|کلمه(?:‌ای)?)[^.!؟]{0,40}اضافه\s*نکن)/u;

const SYSTEM_RULE_ECHO_PATTERN =
  /(?:قواعد\s*(?:قطعی|سیستم)|شما فقط موتور استخراج|دستورهای داخلی|خروجی فقط یک JSON|JSON object|itemType فقط یکی|needsHumanSupport\s*=|measurementStatus یکی از|currentDraftItems|moreItemsAnswer را|reply فقط|max_completion_tokens|بند\s*\d+\s*[:.)])/u;

const WELCOME_BLOCK_PATTERN =
  /(?:به قالیشور خوش آمدید|کارشناس هوشمند قدم به قدم)/u;

const INTRO_EXAMPLE_PATTERN =
  /مثلاً بگویید:\s*دو\s*(?:تخته\s*)?فَ?رش ماشینی/u;

export function isInternalSpeechTrainingText(value: string): boolean {
  const text = value.trim();
  if (!text) return false;
  return PRONUNCIATION_TEACHING_PATTERN.test(text) || SYSTEM_RULE_ECHO_PATTERN.test(text);
}

const ADDING_ANOTHER_ITEM_PATTERN =
  /(?:موکت|پتو|پرده|مبل|تشک|فرش|گلیم|گبه|خوشخواب).{0,24}(?:دارم|اضافه|هم\s*(?:هست|بده|بگذار))/u;

const DECLINE_PHRASE_PATTERN =
  /نمی\s*خوا(?:م|هم)|نمیخوام|لازم نیست|کافی(?:ه| است)|بس(?:ه| است)|تمام(?:ه| است)?|همین(?:ا|‌?ها)?(?:ست|است)?|مورد دیگری ندارم|چیز(?:ی)?\s*(?:دیگه|دیگر)\s*ندارم|دیگه\s*(?:نمی|لازم|چیزی\s*نیست)/u;

const STANDALONE_NO_PATTERN = /^(?:نه|خیر)(?:[\s،.!؟]*)$/u;
const NO_THEN_DECLINE_PATTERN = /^(?:نه|خیر)(?:[\s،.!؟]+)/u;
const NINE_COUNT_PATTERN = /(?:^|[\s،.])نه\s*(?:تخته|تا|دانه|عدد|دست|سرویس)/u;

const ADVANCE_PAST_MORE_ITEMS_PATTERN =
  /(?:ادامه(?:\s*(?:بده|بدیم|ده))?|برو\s*(?:جلو|بعدی|مرحله\s*بعد)|مرحله\s*بعد|بریم(?:\s*(?:بعد|جلو|مرحله\s*بعد))?|بگذار\s*بریم|ثبت(?:ش)?\s*کن|تکمیل(?:ش)?\s*کن|همین\s*کافیست)/u;

const MATCHING_CHOICE_ONLY_PATTERN =
  /^(?:سریع‌?تر|زودتر|فوری|اقتصادی|ارزان(?:‌?تر)?|متعادل|تضمین(?:\s*بالاتر)?|کیفیت)(?:\s*(?:باشه|باشد|می\s*خوا(?:م|هم)|لطفا)?)?[\s،.!؟]*$/u;

/** True when the customer, in any ordinary phrasing, does not want more services. */
export function isDecliningMoreItems(message: string): boolean {
  const text = normalizePersianOrderText(message);
  if (!text) return false;
  if (ADDING_ANOTHER_ITEM_PATTERN.test(text)) return false;
  if (NINE_COUNT_PATTERN.test(text)) return false;
  if (STANDALONE_NO_PATTERN.test(text)) return true;
  if (DECLINE_PHRASE_PATTERN.test(text)) return true;
  if (ADVANCE_PAST_MORE_ITEMS_PATTERN.test(text)) return true;
  return NO_THEN_DECLINE_PATTERN.test(text) && !/(?:موکت|پتو|پرده|مبل|تشک|فرش|گلیم|گبه|خوشخواب)/u.test(text);
}

const MATCHING_PREFERENCE_QUESTION_PATTERN =
  /(?:یعنی\s*(?:چی|چه)|فرق(?:ش|شان)?\s*(?:چی|چیه|چیست)?|منظور(?:تون|تان)?|چطور انتخاب|کدام بهتر)|(?:اقتصادی|سریع|تضمین|متعادل|ارزان|کیفیت).{0,16}(?:یعنی\s*(?:چی|چه)|فرق|چیست|چیه)/u;

export function isMatchingPreferenceQuestion(message: string): boolean {
  const text = normalizePersianOrderText(message);
  if (!text || MATCHING_CHOICE_ONLY_PATTERN.test(text)) return false;
  return MATCHING_PREFERENCE_QUESTION_PATTERN.test(text);
}

export const MATCHING_PREFERENCE_EXPLAIN =
  "سریع‌تر یعنی قالیشویی زودتر کار را تمام می‌کند. اقتصادی یعنی هزینه کمتر اولویت است. تضمین بالاتر یعنی کیفیت و اعتبار مهم‌تر است. متعادل بین زمان، قیمت و کیفیت است. کدام را می‌خواهید؟";

function stripTrainingSpans(value: string): string {
  const sentences = value
    .split(/\n+/u)
    .flatMap((line) => line.split(/(?<=[.!؟])\s+/u))
    .map((part) => part.trim())
    .filter(Boolean);
  const kept = sentences.filter((part) => !isInternalSpeechTrainingText(part));
  let text = kept.join(" ").trim();
  if (!text && sentences.length === 1 && isInternalSpeechTrainingText(sentences[0]!)) {
    text = sentences[0]!
      .replace(/^[\s\S]*?تلفظ\s*کن(?:ید)?[.!؟]?\s*/u, "")
      .trim();
  }
  if (isInternalSpeechTrainingText(text)) return "";
  return text;
}

function lastAssistantText(
  history: readonly OrderAssistantConversationMessage[] | undefined,
): string | null {
  if (!history) return null;
  for (let index = history.length - 1; index >= 0; index -= 1) {
    if (history[index]?.role === "assistant") return history[index]!.text;
  }
  return null;
}

export function sanitizeCustomerSpokenReply(input: {
  readonly reply: string;
  readonly conversationTurn?: number;
  readonly lastAssistantReply?: string | null;
  readonly conversationHistory?: readonly OrderAssistantConversationMessage[];
  readonly fallback?: string;
}): string {
  const fallback = input.fallback === undefined
    ? DEFAULT_CUSTOMER_ORDER_QUESTION
    : input.fallback.trim();
  const safeFallback = fallback && !isInternalSpeechTrainingText(fallback)
    ? fallback
    : input.fallback === undefined
      ? DEFAULT_CUSTOMER_ORDER_QUESTION
      : "";
  const turn = input.conversationTurn ?? 1;
  const previous = input.lastAssistantReply ?? lastAssistantText(input.conversationHistory);

  let text = stripTrainingSpans(input.reply);
  if (isInternalSpeechTrainingText(text) || SYSTEM_RULE_ECHO_PATTERN.test(text)) {
    text = "";
  }
  if (WELCOME_BLOCK_PATTERN.test(text) && turn > 1) {
    text = text
      .replace(/[^.!؟\n]*?(?:به قالیشور خوش آمدید|کارشناس هوشمند قدم به قدم)[^.!؟\n]*[.!؟]?/gu, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  if (turn > 1) {
    text = text.replace(/^سلام(?:[\s،.!؟]|$)+/u, "").trim();
    if (INTRO_EXAMPLE_PATTERN.test(text)) {
      text = text.replace(/[^.!؟\n]*مثلاً بگویید:[^.!؟\n]*[.!؟]?/gu, " ").replace(/\s+/g, " ").trim();
    }
  }
  if (!text) text = safeFallback;
  if (previous && text && normalizePersianOrderText(text) === normalizePersianOrderText(previous)) {
    if (safeFallback && normalizePersianOrderText(safeFallback) !== normalizePersianOrderText(previous)) {
      text = safeFallback;
    }
  }
  return text;
}

/** Diacritics for the speech engine only; never a customer-facing teaching sentence. */
export function applyPersianSpeechDiacritics(value: string): string {
  return value
    .replace(
      /پس هیچ مورد دیگری برای دریافت خدمات قالیشور ندارید؟ اگر نه که برای تکمیل ثبت سفارش بریم مرحله بعد\./g,
      "پَس هیچ مُورِدِ دیگَری بَرایِ دَریافْتِ خَدَماتِ قالیشور نَدارید؟ اَگَر نَه کِه بَرایِ تَکمیلِ ثَبْتِ سِفارِش بِریم مَرحَلِهٔ بَعْد.",
    )
    .replace(/فرش/g, "فَرش")
    .replace(/مُ?وکِ?ت/g, "موکِت")
    .replace(/مطمئن/g, "مُطْمَئِن");
}
